/**
 * File d'attente des photos de chantier.
 * Zone de transit privee (IndexedDB) : la photo y reste le temps de partir
 * vers le serveur, puis est effacee. Rien n'est enregistre dans la galerie
 * du telephone. Purge automatique de tout ce qui depasse 48 h.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const BASE = 'eco_photos';
const STORE = 'attente';
const DUREE_MAX_MS = 48 * 60 * 60 * 1000;

let dbPromesse = null;

const ouvrirBase = () => {
  if (dbPromesse) return dbPromesse;
  dbPromesse = new Promise((ok, ko) => {
    const req = indexedDB.open(BASE, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => ok(req.result);
    req.onerror = () => ko(req.error);
  });
  return dbPromesse;
};

const tx = async (mode, action) => {
  const db = await ouvrirBase();
  return new Promise((ok, ko) => {
    const t = db.transaction(STORE, mode);
    const req = action(t.objectStore(STORE));
    t.oncomplete = () => ok(req?.result);
    t.onerror = () => ko(t.error);
  });
};

const abonnes = new Set();
export const surChangement = (fn) => {
  abonnes.add(fn);
  return () => abonnes.delete(fn);
};
const prevenir = () => abonnes.forEach((fn) => { try { fn(); } catch (e) {} });

export const listerEnAttente = async (chantierId) => {
  const tout = (await tx('readonly', (s) => s.getAll())) || [];
  return chantierId ? tout.filter((p) => p.chantierId === chantierId) : tout;
};

export const ajouterPhoto = async (chantierId, typePhoto, fichier) => {
  const item = {
    id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    chantierId,
    typePhoto,
    fichier,
    date: new Date().toISOString(),
    horodatage: Date.now()
  };
  await tx('readwrite', (s) => s.put(item));
  prevenir();
  lancerEnvoi();
  return item;
};

export const retirer = async (id) => {
  await tx('readwrite', (s) => s.delete(id));
  prevenir();
};

// Purge de tout ce qui traine depuis plus de 48 h
export const purger = async () => {
  const tout = await listerEnAttente();
  const limite = Date.now() - DUREE_MAX_MS;
  let supprimees = 0;
  for (const item of tout) {
    if ((item.horodatage || 0) < limite) {
      await tx('readwrite', (s) => s.delete(item.id));
      supprimees++;
    }
  }
  if (supprimees) prevenir();
  return supprimees;
};

let enCours = false;

export const lancerEnvoi = async () => {
  if (enCours || !navigator.onLine) return;
  enCours = true;
  try {
    let liste = await listerEnAttente();
    while (liste.length > 0) {
      const item = liste[0];
      try {
        const data = new FormData();
        data.append('type_photo', item.typePhoto);
        data.append('fichier', item.fichier, 'photo.jpg');
        await axios.post(`${API}/api/photos/${item.chantierId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        await tx('readwrite', (s) => s.delete(item.id));
        prevenir();
      } catch (e) {
        const code = e.response?.status;
        if ([400, 403, 404].includes(code)) {
          // Erreur definitive : inutile de reessayer
          await tx('readwrite', (s) => s.delete(item.id));
          prevenir();
        } else {
          break; // Probleme reseau : on reessaiera
        }
      }
      liste = await listerEnAttente();
    }
  } finally {
    enCours = false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', lancerEnvoi);
  setInterval(lancerEnvoi, 15000);
  purger();
  setInterval(purger, 60 * 60 * 1000);
}
